import { motion } from 'framer-motion'
import { Badge, Tag } from './ui'
import { useT } from '../lib/i18n'
import type { Locale, ParadigmLocale, ParadigmsPageLocale } from '../lib/locales'

interface ParadigmsPageProps {
  onEnterDemo: () => void
  onOpenLab: (paradigmId: string) => void
}

const GROUP_ORDER: Array<ParadigmLocale['group']> = ['input', 'runtime', 'output']

const GROUP_ACCENT: Record<
  ParadigmLocale['group'],
  { dot: string; ring: string; tone: 'warn' | 'info' | 'process' }
> = {
  input: { dot: 'bg-[color:var(--color-orion-amber)]', ring: 'border-[color:var(--color-orion-amber)]/40', tone: 'warn' },
  runtime: { dot: 'bg-[color:var(--color-orion-cyan)]', ring: 'border-[color:var(--color-orion-cyan)]/40', tone: 'info' },
  output: { dot: 'bg-[color:var(--color-orion-violet)]', ring: 'border-[color:var(--color-orion-violet)]/40', tone: 'process' },
}

export function ParadigmsPage({ onEnterDemo, onOpenLab }: ParadigmsPageProps) {
  const t = useT()
  const p = t.paradigms
  const tryItLabel = t.lab.tryItLabel

  return (
    <main className="mx-auto max-w-6xl px-6 sm:px-10 pt-16 sm:pt-24 pb-24">
      <ParadigmsHero p={p} />

      <div className="space-y-24">
        {GROUP_ORDER.map((group) => (
          <ParadigmGroupSection
            key={group}
            group={group}
            p={p}
            tryItLabel={tryItLabel}
            onOpenLab={onOpenLab}
          />
        ))}
      </div>

      <BottomCta
        p={p}
        onEnterDemo={onEnterDemo}
        onOpenLab={() => onOpenLab(p.paradigms[0]?.id ?? 'event-driven')}
        labLabel={t.nav.lab}
      />
    </main>
  )
}

function ParadigmsHero({ p }: { p: ParadigmsPageLocale }) {
  return (
    <section className="mb-20 sm:mb-28">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col gap-5 max-w-4xl"
      >
        <Tag kind="rule">{p.sectionTag}</Tag>
        <h1
          className="text-[color:var(--color-orion-text)] leading-[1.05]"
          style={{ fontSize: 'var(--text-h1)', fontWeight: 600 }}
        >
          {p.heroTitleA}
          <span className="text-[color:var(--color-orion-amber)]">{p.heroTitleSpan}</span>
          {p.heroTitleB}
        </h1>
        <p
          className="text-[color:var(--color-orion-haze)] max-w-3xl"
          style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
        >
          {p.heroSubtitle}
        </p>
      </motion.div>
    </section>
  )
}

function ParadigmGroupSection({
  group,
  p,
  tryItLabel,
  onOpenLab,
}: {
  group: ParadigmLocale['group']
  p: ParadigmsPageLocale
  tryItLabel: string
  onOpenLab: (id: string) => void
}) {
  const accent = GROUP_ACCENT[group]
  const groupParadigms = p.paradigms.filter((pp) => pp.group === group)

  return (
    <section>
      <header className="mb-8 flex items-baseline gap-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className={`size-2.5 rounded-full ${accent.dot}`} />
          <span className="mono text-[length:var(--text-small)] uppercase tracking-widest text-[color:var(--color-orion-text)]">
            {p.groupLabels[group]}
          </span>
        </div>
        <p className="text-[color:var(--color-orion-haze)] text-[length:var(--text-small)] max-w-2xl">
          {p.groupDescriptions[group]}
        </p>
      </header>

      <div className="space-y-4">
        {groupParadigms.map((paradigm, idx) => (
          <ParadigmCard
            key={paradigm.id}
            paradigm={paradigm}
            scenariosLabel={p.scenariosLabel}
            interactionLabel={p.interactionLabel}
            tryItLabel={tryItLabel}
            onOpenLab={onOpenLab}
            index={idx}
          />
        ))}
      </div>
    </section>
  )
}

interface ParadigmCardProps {
  paradigm: ParadigmLocale
  scenariosLabel: string
  interactionLabel: string
  tryItLabel: string
  onOpenLab: (id: string) => void
  index: number
}

function ParadigmCard({
  paradigm,
  scenariosLabel,
  interactionLabel,
  tryItLabel,
  onOpenLab,
  index,
}: ParadigmCardProps) {
  const accent = GROUP_ACCENT[paradigm.group]
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className={`surface-night border-faint rounded-[var(--radius-lg)] p-6 sm:p-7 transition-colors hover:${accent.ring}`}
    >
      <div className="grid sm:grid-cols-[80px_1fr] gap-4 sm:gap-6">
        <div className="flex sm:flex-col items-baseline sm:items-start gap-3 sm:gap-1">
          <span className="mono text-[28px] sm:text-[36px] leading-none text-[color:var(--color-orion-amber)] font-light">
            {paradigm.number}
          </span>
          <span className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)]">
            {paradigm.group === 'input' ? 'INPUT' : paradigm.group === 'runtime' ? 'RUNTIME' : 'OUTPUT'}
          </span>
        </div>

        <div className="min-w-0">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
            <h3
              className="text-[color:var(--color-orion-text)] font-medium leading-tight"
              style={{ fontSize: 'var(--text-h3)' }}
            >
              {paradigm.title}
            </h3>
            <div className="flex items-center gap-2 shrink-0 mt-1">
              <Badge tone={accent.tone}>{paradigm.triggerBadge}</Badge>
              <span className="text-[color:var(--color-orion-haze)] mono text-[length:var(--text-micro)]">→</span>
              <Badge tone="pass">{paradigm.outputBadge}</Badge>
            </div>
          </div>

          <p
            className="text-[color:var(--color-orion-haze)] mb-5"
            style={{ fontSize: 'var(--text-body)', lineHeight: 1.55 }}
          >
            {paradigm.tagline}
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2.5">
                {scenariosLabel}
              </div>
              <ul className="space-y-1.5">
                {paradigm.scenarios.map((sc, i) => (
                  <li key={i} className="flex gap-2 text-[length:var(--text-small)]">
                    <span className="mono text-[color:var(--color-orion-haze)] shrink-0 min-w-[90px]">
                      {sc.industry}
                    </span>
                    <span className="text-[color:var(--color-orion-text)] leading-snug">
                      {sc.example}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-2.5">
                {interactionLabel}
              </div>
              <ul className="space-y-1.5">
                {paradigm.interactionNotes.map((note, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-[length:var(--text-small)] text-[color:var(--color-orion-text)] leading-snug"
                  >
                    <span className="text-[color:var(--color-orion-haze)] shrink-0">·</span>
                    <span>{note}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-5 pt-4 border-t border-[color:var(--color-orion-surface)]/40 flex justify-end">
            <button
              type="button"
              onClick={() => onOpenLab(paradigm.id)}
              className="mono text-[length:var(--text-micro)] uppercase tracking-widest px-3 py-1.5 rounded-[var(--radius-sm)] border border-[color:var(--color-orion-amber)]/50 bg-[color:var(--color-orion-amber)]/12 text-[color:var(--color-orion-amber)] hover:bg-[color:var(--color-orion-amber)]/22 transition-colors"
            >
              {tryItLabel}
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  )
}

function BottomCta({
  p,
  onEnterDemo,
  onOpenLab,
  labLabel,
}: {
  p: Locale['paradigms']
  onEnterDemo: () => void
  onOpenLab: () => void
  labLabel: string
}) {
  return (
    <section className="mt-24 sm:mt-32">
      <div className="surface-night border-faint rounded-[var(--radius-lg)] p-8 sm:p-12 text-center">
        <div className="flex justify-center mb-4">
          <Tag kind="rule">{p.bottomCta.sectionTag}</Tag>
        </div>
        <h2
          className="text-[color:var(--color-orion-text)] mb-2"
          style={{ fontSize: 'var(--text-h2)', fontWeight: 600 }}
        >
          {p.bottomCta.titleLine1}
        </h2>
        <p
          className="text-[color:var(--color-orion-amber)] mb-6"
          style={{ fontSize: 'var(--text-lead)' }}
        >
          {p.bottomCta.titleLine2}
        </p>
        <p
          className="text-[color:var(--color-orion-haze)] max-w-3xl mx-auto mb-8"
          style={{ fontSize: 'var(--text-body)', lineHeight: 1.6 }}
        >
          {p.bottomCta.body}
        </p>
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onEnterDemo}
            className="mono text-[length:var(--text-small)] uppercase tracking-widest px-6 py-3 rounded-[var(--radius-md)] bg-[color:var(--color-orion-amber)] text-[color:var(--color-orion-deep)] font-medium hover:bg-[color:var(--color-orion-amber-dim)] transition-colors"
          >
            {p.bottomCta.button} →
          </button>
          <button
            type="button"
            onClick={onOpenLab}
            className="mono text-[length:var(--text-small)] uppercase tracking-widest px-6 py-3 rounded-[var(--radius-md)] border border-[color:var(--color-orion-amber)]/60 bg-[color:var(--color-orion-amber)]/12 text-[color:var(--color-orion-amber)] hover:bg-[color:var(--color-orion-amber)]/22 transition-colors"
          >
            {labLabel} →
          </button>
        </div>
      </div>
    </section>
  )
}
